import * as React from 'react';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {DripSequenceListItem} from 'shells/drip-sequence-list-item/drip-sequence-list-item';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {
    DripSequenceStubFragment as DripSequence,
    EditionStubFragment as DripEdition,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {NewEditionModal} from '../editions/new-edition-modal';
import {DripSequenceEditionMutations} from '../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-mutations';
import {DripSequenceEditionStubsQuery} from '../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-stubs-query';

type OwnProps = {
    dripSequence: DripSequence;
    isArchived: boolean;
    onDeleteDripSequence: (dripSequenceId: string) => Promise<any>;
    onDuplicateDripSequence?: (dripSequenceId: string) => any;
    onRestoreDripSequence: (dripSequenceId: string) => Promise<any>;
};

type Props = OwnProps & {
    editions: DripEdition[];
    editionsError: string | null | undefined;
    isLoading: boolean;
    onDuplicateEdition: (editionId: string, campaignId: string, name: string) => Promise<any>;
    onCreateNewMessage: (name: string, campaignId: string) => Promise<any>;
};

function DripSequenceListItemWrapperComponent(props: Props) {
    const [duplicatingEdition, setDuplicatingEdition] = React.useState<
        DripEdition | null | undefined
    >(null);
    const [isDuplicatingEdition, setIsDuplicatingEdition] = React.useState<boolean>(false);
    const [isAddingNewEdition, setIsAddingNewEdition] = React.useState<boolean>(false);

    return (
        <React.Fragment>
            {props.editionsError ? (
                <EmptyStateSmall
                    title={props.editionsError}
                    subtitle={
                        <div className='flex'>
                            Please&nbsp;
                            <ButtonMover size='xlarge' direction={['left', 'right', 'up', 'down']}>
                                <Button
                                    size='xlarge'
                                    variant='text-primary'
                                    onClick={() => {
                                        location.reload();
                                    }}
                                >
                                    try again
                                </Button>
                            </ButtonMover>
                            &nbsp;or&nbsp;
                            <Link href='https://www.nutshell.com/support' variant='primary'>
                                contact support.
                            </Link>
                        </div>
                    }
                    icon={
                        <div style={{width: '150px'}}>
                            <Illustration src='plug-error-state' width='100%' height='100%' />
                        </div>
                    }
                />
            ) : (
                <DripSequenceListItem
                    dripSequence={{
                        id: props.dripSequence.id,
                        name: props.dripSequence.name,
                        htmlUrlPath: props.dripSequence.htmlUrlPath,
                        editions: props.dripSequence.editions,
                        status: props.dripSequence.status,
                        startTrigger: props.dripSequence.startTrigger
                            ? {
                                  humanReadableTitle:
                                      props.dripSequence.startTrigger.humanReadableTitle,
                                  humanReadableSubtitle:
                                      props.dripSequence.startTrigger.humanReadableSubtitle,
                                  id: props.dripSequence.startTrigger.id,
                              }
                            : undefined,
                        createdTime: props.dripSequence.createdTime,
                        lastStatusChangeTime: props.dripSequence.lastStatusChangeTime,
                        deletedTime: props.dripSequence.deletedTime,
                        totalRecipientCount:
                            props.dripSequence.engagementStats.dripProgressionTotalRecipients,
                        isRemovable: props.dripSequence.isRemovable,
                    }}
                    editions={props.editions.map((edition) => {
                        return {
                            id: edition.id,
                            htmlUrlPath: edition.htmlUrlPath,
                            name: edition.name,
                            secondaryInfo:
                                edition.emailTemplate && edition.emailTemplate.subject ? (
                                    edition.emailTemplate.subject
                                ) : (
                                    <div style={{fontStyle: 'italic'}}>No subject</div>
                                ),
                            status: edition.status,
                            backgroundColor: edition.backgroundColor,
                        };
                    })}
                    isArchived={props.isArchived}
                    isLoading={props.isLoading}
                    onDeleteDripSequence={props.onDeleteDripSequence}
                    onRestoreDripSequence={props.onRestoreDripSequence}
                    onDuplicateDripSequence={props.onDuplicateDripSequence}
                />
            )}
            {duplicatingEdition ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    initialValue={`Copy of ${duplicatingEdition.name}`}
                    title='Name your new edition'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder={`Copy of ${duplicatingEdition.name}`}
                    isSaving={isDuplicatingEdition}
                    onClose={() => {
                        setDuplicatingEdition(null);
                    }}
                    onSave={(name) => {
                        setIsDuplicatingEdition(true);
                        const nameValue = name && name.length ? name : null;
                        if (nameValue) {
                            props
                                .onDuplicateEdition(
                                    duplicatingEdition.id,
                                    props.dripSequence.id,
                                    nameValue
                                )
                                .then(() => {
                                    setIsDuplicatingEdition(false);
                                    setDuplicatingEdition(null);
                                });
                        }
                    }}
                />
            ) : undefined}
            {isAddingNewEdition && !props.isArchived ? (
                <NewEditionModal
                    type='dripSequenceEditions'
                    campaignId={props.dripSequence.id}
                    dripSequenceId={props.dripSequence.id}
                    isOpen={isAddingNewEdition}
                    onClose={() => {
                        setIsAddingNewEdition(false);
                    }}
                    onCreateEdition={
                        props.onCreateNewMessage
                            ? props.onCreateNewMessage
                            : () => {
                                  return new Promise(() => {});
                              }
                    }
                    onDuplicateEdition={props.onDuplicateEdition}
                />
            ) : undefined}
        </React.Fragment>
    );
}

export function DripSequenceListItemWrapper(props: OwnProps) {
    return (
        <DripSequenceEditionStubsQuery
            dripSequenceId={props.dripSequence.id}
            key={props.dripSequence.id}
        >
            {({editions, isLoading, error}) => (
                <DripSequenceEditionMutations>
                    {({onCreate, onDuplicate}) => (
                        <DripSequenceListItemWrapperComponent
                            dripSequence={props.dripSequence}
                            editions={editions}
                            isArchived={props.isArchived}
                            onDeleteDripSequence={props.onDeleteDripSequence}
                            onDuplicateDripSequence={props.onDuplicateDripSequence}
                            onRestoreDripSequence={props.onRestoreDripSequence}
                            onDuplicateEdition={(editionId, campaignId, name) =>
                                onDuplicate(editionId, campaignId, name).then((newEdition) => {
                                    if (newEdition) appHistory.push(newEdition.htmlUrlPath);
                                })
                            }
                            onCreateNewMessage={(name, campaignId) =>
                                onCreate(name, campaignId).then((newEdition) => {
                                    if (newEdition) appHistory.push(newEdition.htmlUrlPath);
                                })
                            }
                            isLoading={isLoading}
                            editionsError={error}
                        />
                    )}
                </DripSequenceEditionMutations>
            )}
        </DripSequenceEditionStubsQuery>
    );
}
